import { useEffect, useCallback, useMemo } from 'react';
import { CrispService } from './crispService';
import { useMeQuery } from '../../hooks/useMeQuery';
import { getGravityUserId, getGravityUserStatus } from '../../hooks/useAuth';

/**
 * React hook to manage Crisp chat integration
 * 
 * Usage:
 * ```tsx
 * const { openChat, isInitialized } = useCrispChat();
 * 
 * <Button onClick={openChat}>Help</Button>
 * ```
 */
export function useCrispChat() {
  const { data: me } = useMeQuery();
  const crispService = useMemo(() => CrispService.getInstance(), []);
  const websiteId = import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined;
  const tokenId = useMemo(() => {
    const userId = getGravityUserId();
    return userId ? btoa(userId) : undefined;
  }, []);

  // Initialize Crisp on mount with user ID from gravity token
  useEffect(() => {
    if (!websiteId) {
      console.warn('[useCrispChat] VITE_CRISP_WEBSITE_ID not configured');
      return;
    }

    crispService.initialize(websiteId, tokenId);
  }, [crispService, tokenId, websiteId]);

  // Update user info when user data is fetched from API
  // Skip for demo users
  useEffect(() => {
    const userStatus = getGravityUserStatus();
    const isDemoUser = userStatus === 'DEMO';
    
    if (me?.name && me?.email && crispService.getIsInitialized() && !isDemoUser) {
      crispService.setUser(me.name, me.email);
    }
  }, [me?.name, me?.email, crispService]);

  /**
   * Open Crisp chat window
   */
  const openChat = useCallback(async () => {
    if (!websiteId) {
      console.warn('[useCrispChat] Cannot open chat: VITE_CRISP_WEBSITE_ID not configured');
      return;
    }

    if (!crispService.getIsInitialized()) {
      crispService.initialize(websiteId, tokenId);
    }

    await crispService.openChat();
  }, [crispService, tokenId, websiteId]);

  return {
    openChat,
    isInitialized: crispService.getIsInitialized(),
  };
}
